/* =================================================================================================
 * Shopify Inbox "Live chat" button – Masterclip customisation
 * =================================================================================================
 * Works together with chat-button.css.
 *
 *  1. Adds body.mc-drawer-open while any theme drawer (cart, quick-add, filters…) is open. The CSS
 *     uses this to hide the chat button so it can't overlap the Checkout / Add to cart buttons.
 *  2. Makes the chat button draggable (mouse or touch). The position is remembered in localStorage
 *     so it stays where the customer left it on every page. A short tap/click still opens the chat.
 *
 * The chat widget is a <shopify-chat> custom element injected by the Shopify Inbox app embed. Its
 * button carries part="activator". This script only listens on the host element and reads the
 * button's size/position, so it keeps working if Shopify re-renders the widget's internals.
 * ------------------------------------------------------------------------------------------------- */
(function () {
  'use strict';

  var STORAGE_KEY = 'mc-chat-button-position';
  var DRAG_THRESHOLD = 6;   // px of movement before a press counts as a drag rather than a tap
  var EDGE_MARGIN = 8;      // px – the button is always kept at least this far inside the viewport

  var host = null;          // the <shopify-chat> element
  var savedPos = loadPosition(); // { fx, fy } – button centre as a fraction of the viewport, or null
  var drag = null;          // state for the drag in progress
  var suppressClick = false;

  /* ---------------------------------------------------------------------------------------------
   * 1. Hide the chat button while a drawer is open
   * --------------------------------------------------------------------------------------------- */
  function syncDrawerState() {
    var open = !!document.querySelector('.drawer[open]');
    document.body.classList.toggle('mc-drawer-open', open);
  }

  function watchDrawers() {
    // Every side drawer in this theme (cart-drawer, quick-add-drawer, facet-filters…) gets an
    // `open` attribute while it is showing, so one observer covers all of them.
    var observer = new MutationObserver(syncDrawerState);
    observer.observe(document.body, { attributes: true, attributeFilter: ['open'], subtree: true });
    syncDrawerState();
  }

  /* ---------------------------------------------------------------------------------------------
   * 2. Draggable chat button
   * --------------------------------------------------------------------------------------------- */
  function loadPosition() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      var pos = JSON.parse(raw);
      if (typeof pos.fx !== 'number' || typeof pos.fy !== 'number') return null;
      return pos;
    } catch (e) {
      return null;
    }
  }

  function savePosition(pos) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(pos));
    } catch (e) {
      /* localStorage unavailable (e.g. private mode) – position just won't persist */
    }
  }

  /**
   * Finds the element with part="activator" inside a shadow root. Once the chat has been opened,
   * Shopify swaps in a nested <shopify-agent-runtime> element (with its own shadow root) that
   * re-exports the part, so nested roots are searched too.
   */
  function findActivatorIn(root, depth) {
    if (!root || depth > 4) return null;
    var found = root.querySelector('[part~="activator"]');
    if (found) return found;
    var exporters = root.querySelectorAll('[exportparts]');
    for (var i = 0; i < exporters.length; i += 1) {
      found = findActivatorIn(exporters[i].shadowRoot, depth + 1);
      if (found) return found;
    }
    return null;
  }

  /** Returns the chat button element (part="activator"), from an event path if one is given. */
  function getActivator(evt) {
    if (evt && evt.composedPath) {
      var path = evt.composedPath();
      for (var i = 0; i < path.length; i += 1) {
        var el = path[i];
        if (el && el.part && el.part.contains && el.part.contains('activator')) return el;
      }
    }
    return host ? findActivatorIn(host.shadowRoot, 0) : null;
  }

  function getOffset() {
    return {
      dx: parseFloat(host.style.getPropertyValue('--mc-chat-dx')) || 0,
      dy: parseFloat(host.style.getPropertyValue('--mc-chat-dy')) || 0
    };
  }

  function setOffset(dx, dy) {
    host.style.setProperty('--mc-chat-dx', Math.round(dx) + 'px');
    host.style.setProperty('--mc-chat-dy', Math.round(dy) + 'px');
  }

  /** Keeps a button of size w×h, centred at (cx, cy), fully inside the viewport. */
  function clampCentre(cx, cy, w, h) {
    var minX = EDGE_MARGIN + w / 2;
    var maxX = Math.max(minX, window.innerWidth - EDGE_MARGIN - w / 2);
    var minY = EDGE_MARGIN + h / 2;
    var maxY = Math.max(minY, window.innerHeight - EDGE_MARGIN - h / 2);
    return {
      x: Math.min(Math.max(cx, minX), maxX),
      y: Math.min(Math.max(cy, minY), maxY)
    };
  }

  /**
   * Measures the button and returns its untranslated centre and size, or null if it isn't
   * currently rendered (e.g. the chat window is open, or a drawer is hiding it).
   */
  function measure(btn) {
    if (!btn || !btn.offsetWidth) return null;
    var rect = btn.getBoundingClientRect();
    var offset = getOffset();
    return {
      baseCx: (rect.left + rect.right) / 2 - offset.dx,
      baseCy: (rect.top + rect.bottom) / 2 - offset.dy,
      w: btn.offsetWidth,
      h: btn.offsetHeight
    };
  }

  /** Moves the button to the saved position (clamped to the current viewport). */
  function applySavedPosition() {
    if (!savedPos || drag) return false;
    var m = measure(getActivator());
    if (!m) return false;
    var c = clampCentre(savedPos.fx * window.innerWidth, savedPos.fy * window.innerHeight, m.w, m.h);
    setOffset(c.x - m.baseCx, c.y - m.baseCy);
    return true;
  }

  /**
   * The widget renders its button a little after the <shopify-chat> element appears, and hides it
   * while the chat window is open, so keep trying to restore the position until it succeeds.
   */
  function restorePositionWhenReady() {
    if (!savedPos) return;
    if (applySavedPosition()) return;

    var attempts = 0;
    var timer = setInterval(function () {
      attempts += 1;
      if (applySavedPosition() || attempts > 100) clearInterval(timer); // ~20s
    }, 200);
  }

  function onPointerDown(evt) {
    if (drag || evt.button > 0 || !evt.isPrimary) return;
    var btn = getActivator(evt);
    if (!btn) return;
    var m = measure(btn);
    if (!m) return;

    var offset = getOffset();
    drag = {
      id: evt.pointerId,
      btn: btn,
      startX: evt.clientX,
      startY: evt.clientY,
      dx0: offset.dx,
      dy0: offset.dy,
      baseCx: m.baseCx,
      baseCy: m.baseCy,
      w: m.w,
      h: m.h,
      moved: false
    };

    try { btn.setPointerCapture(evt.pointerId); } catch (e) { /* not supported – fine */ }
    window.addEventListener('pointermove', onPointerMove, true);
    window.addEventListener('pointerup', onPointerEnd, true);
    window.addEventListener('pointercancel', onPointerEnd, true);
  }

  function onPointerMove(evt) {
    if (!drag || evt.pointerId !== drag.id) return;
    var mx = evt.clientX - drag.startX;
    var my = evt.clientY - drag.startY;

    if (!drag.moved) {
      if (Math.abs(mx) < DRAG_THRESHOLD && Math.abs(my) < DRAG_THRESHOLD) return;
      drag.moved = true;
      host.classList.add('mc-chat-dragging');
    }

    evt.preventDefault();
    var c = clampCentre(drag.baseCx + drag.dx0 + mx, drag.baseCy + drag.dy0 + my, drag.w, drag.h);
    setOffset(c.x - drag.baseCx, c.y - drag.baseCy);
  }

  function onPointerEnd(evt) {
    if (!drag || evt.pointerId !== drag.id) return;
    var current = drag;
    drag = null;

    window.removeEventListener('pointermove', onPointerMove, true);
    window.removeEventListener('pointerup', onPointerEnd, true);
    window.removeEventListener('pointercancel', onPointerEnd, true);
    try { current.btn.releasePointerCapture(evt.pointerId); } catch (e) { /* ignore */ }
    host.classList.remove('mc-chat-dragging');

    if (!current.moved) return; // a plain tap/click – let the chat open as normal

    // Remember where it was dropped, as a fraction of the viewport so it adapts to other screens.
    var rect = current.btn.getBoundingClientRect();
    savedPos = {
      fx: ((rect.left + rect.right) / 2) / window.innerWidth,
      fy: ((rect.top + rect.bottom) / 2) / window.innerHeight
    };
    savePosition(savedPos);

    // Swallow the click that follows a drag so the chat window doesn't open.
    suppressClick = true;
    setTimeout(function () { suppressClick = false; }, 500);
  }

  function onClickCapture(evt) {
    if (!suppressClick) return;
    suppressClick = false;
    evt.stopImmediatePropagation();
    evt.preventDefault();
  }

  function onResize() {
    // Re-fit the button to the new viewport (rotation, keyboard, window resize).
    applySavedPosition();
  }

  /* ---------------------------------------------------------------------------------------------
   * Boot
   * --------------------------------------------------------------------------------------------- */
  function initChatButton() {
    host = document.querySelector('shopify-chat');
    if (!host) return false;

    host.addEventListener('pointerdown', onPointerDown, true);
    window.addEventListener('click', onClickCapture, true);
    window.addEventListener('resize', onResize);
    restorePositionWhenReady();
    return true;
  }

  function start() {
    watchDrawers();

    // The chat widget is injected by the Inbox app embed some time after the page loads.
    if (initChatButton()) return;
    var observer = new MutationObserver(function () {
      if (initChatButton()) observer.disconnect();
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
}());
